import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import InscricoesFilters from "@/pages/inscricoes/InscricoesFilters";
import InscricoesTable from "@/pages/inscricoes/InscricoesTable";
import {
  DeleteInscricaoDialog, BulkDeleteInscricoesDialog,
} from "@/pages/inscricoes/InscricoesDialogs";

export default function InscricoesPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const userPerms = user?.permissoes || [];
  const canEdit = isSuperAdmin || userPerms.includes("inscricoes.edit");
  const canDelete = isSuperAdmin;
  const canDossie = isSuperAdmin || userPerms.includes("inscricoes.view");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [bairro, setBairro] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkDossieLoading, setBulkDossieLoading] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [togglingFav, setTogglingFav] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (status && status !== "all") params.status = status;
    if (bairro) params.bairro = bairro;
    if (favoritesOnly) params.favorites_only = true;
    try {
      const r = await api.get("/inscricoes", { params });
      setItems(r.data.items); setTotal(r.data.total);
      setSelected(new Set());
    } catch (e) {
      logger.error("Falha ao listar inscrições", e);
    }
  }, [q, status, bairro, favoritesOnly]);

  useEffect(() => { load(); }, [load]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const askDelete = (it) => { setToDelete(it); setConfirmText(""); };
  const closeDeleteDialog = () => { setToDelete(null); setConfirmText(""); };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/inscricoes/${toDelete.id}`);
      toast.success(`Inscrição ${r.data.protocolo} excluída. Backup salvo em: ${r.data.backup_path?.split('/').pop() || 'storage'}`);
      closeDeleteDialog();
      load();
    } catch (e) {
      logger.error("Falha ao excluir inscrição", e);
      toast.error(e.response?.data?.detail || "Falha ao excluir");
    } finally { setDeleting(false); }
  };

  const confirmBulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/inscricoes/bulk-delete", { ids });
      toast.success(`${r.data.removidos} inscrições excluídas. Backup consolidado salvo.`);
      setBulkOpen(false);
      setBulkConfirm("");
      load();
    } catch (e) {
      logger.error("Falha no bulk delete", e);
      toast.error(e.response?.data?.detail || "Falha ao excluir em lote");
    } finally { setBulkDeleting(false); }
  };

  const baixarDossiesEmLote = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkDossieLoading(true);
    try {
      const resp = await api.post("/inscricoes/dossie-bulk", { ids }, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[-:.]/g, "").slice(0, 15);
      a.download = `dossies-${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`ZIP com ${ids.length} dossiê(s) baixado.`);
    } catch (e) {
      logger.error("Falha no dossie bulk", e);
      const msg = e.response?.data?.detail
        || (e.response?.data instanceof Blob ? "Falha ao gerar dossiês" : "Falha ao gerar dossiês");
      toast.error(msg);
    } finally { setBulkDossieLoading(false); }
  };

  const toggleFavorite = async (it) => {
    setTogglingFav(it.id);
    try {
      const r = await api.post(`/inscricoes/${it.id}/favorite`);
      setItems((prev) => prev.map((x) => x.id === it.id
        ? { ...x, is_favorite: r.data.is_favorite }
        : x));
      toast.success(r.data.is_favorite
        ? `★ ${it.protocolo} marcada como favorita`
        : `☆ ${it.protocolo} removida dos favoritos`);
    } catch (e) {
      logger.error("Falha ao favoritar", e);
      toast.error(e.response?.data?.detail || "Falha ao favoritar");
    } finally { setTogglingFav(null); }
  };

  const bulkPreview = useMemo(
    () => items.filter((i) => selected.has(i.id)).slice(0, 20),
    [items, selected],
  );

  return (
    <div className="space-y-6" data-testid="inscricoes-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6]">Inscrições</h1>
        <p className="text-sm text-slate-600">{total} inscrição(ões) encontrada(s)</p>
      </div>

      <InscricoesFilters
        q={q} setQ={setQ}
        bairro={bairro} setBairro={setBairro}
        status={status} setStatus={setStatus}
        onFiltrar={load}
        canDossie={canDossie}
        favoritesOnly={favoritesOnly}
        setFavoritesOnly={setFavoritesOnly}
      />

      <InscricoesTable
        items={items}
        q={q}
        selected={selected}
        togglingFav={togglingFav}
        bulkDossieLoading={bulkDossieLoading}
        canDossie={canDossie}
        canEdit={canEdit}
        canDelete={canDelete}
        onToggleOne={toggleOne}
        onToggleAll={toggleAll}
        onToggleFav={toggleFavorite}
        onAskDelete={askDelete}
        onClearSelection={() => setSelected(new Set())}
        onBulkDossie={baixarDossiesEmLote}
        onBulkDelete={() => { setBulkOpen(true); setBulkConfirm(""); }}
      />

      <DeleteInscricaoDialog
        toDelete={toDelete}
        confirmText={confirmText}
        setConfirmText={setConfirmText}
        deleting={deleting}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
      />

      <BulkDeleteInscricoesDialog
        open={bulkOpen}
        onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkConfirm(""); } }}
        selected={selected}
        bulkPreview={bulkPreview}
        bulkConfirm={bulkConfirm}
        setBulkConfirm={setBulkConfirm}
        bulkDeleting={bulkDeleting}
        onConfirm={confirmBulkDelete}
      />
    </div>
  );
}
